/**
 * Email identity helpers for admin user management.
 *
 * Supabase Auth (GoTrue) resolves emails case-insensitively, but this app
 * historically stored whatever the admin typed — prod contains rows like
 * `MMcCalla@lakelandautomall.com`. That mismatch meant a differently-cased
 * retype looked like a new user to the UI while GoTrue mapped it onto the
 * SAME auth user, so the follow-up insert collided on
 * `uq_user_approvals_tenant_user (tenant_id, user_id)`.
 *
 * Every comparison and every write must go through `normalizeEmail` so the
 * UI's notion of "same user" matches GoTrue's.
 */

/**
 * Consumer mailbox providers. A shared domain like gmail.com tells us nothing
 * about which tenant a person belongs to, so domain-based tenant inference
 * must skip these entirely rather than guess from whoever signed up first.
 */
const FREE_EMAIL_DOMAINS = new Set([
  'gmail.com',
  'googlemail.com',
  'yahoo.com',
  'ymail.com',
  'outlook.com',
  'hotmail.com',
  'live.com',
  'msn.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'aol.com',
  'protonmail.com',
  'proton.me',
  'gmx.com',
  'mail.com',
  'zoho.com',
  'yandex.com',
  'comcast.net',
  'verizon.net',
  'att.net',
  'sbcglobal.net',
  'bellsouth.net',
  'cox.net',
  'charter.net',
  'earthlink.net'
]);

/**
 * Practical email shape check. Deliberately not RFC 5322 — that grammar
 * accepts addresses no mail server here will ever route. This rejects the
 * mistakes admins actually make: missing @, missing TLD, stray whitespace,
 * doubled dots.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;

/** Trim and lowercase. Use before EVERY comparison and every write. */
export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

/** True when the address is well-formed enough to attempt an invite. */
export function isValidEmail(email: string): boolean {
  const normalized = normalizeEmail(email);
  if (!normalized || normalized.length > 254) return false;
  if (normalized.includes('..')) return false;
  return EMAIL_PATTERN.test(normalized);
}

/** Returns the lowercased domain, or undefined when the address is unusable. */
export function getEmailDomain(email: string): string | undefined {
  const normalized = normalizeEmail(email);
  const at = normalized.lastIndexOf('@');
  if (at < 1 || at === normalized.length - 1) return undefined;
  return normalized.slice(at + 1);
}

/** True for consumer mailbox providers, which carry no tenant signal. */
export function isFreeEmailDomain(domain: string): boolean {
  return FREE_EMAIL_DOMAINS.has(domain.toLowerCase());
}

/**
 * Extracts the registrable-ish domain from a website URL so a tenant's own
 * `website` field can be matched against a user's email domain.
 * Handles bare hosts, full URLs, and a leading `www.`.
 */
export function getDomainFromWebsite(website: string | undefined): string | undefined {
  if (!website) return undefined;
  const trimmed = website.trim().toLowerCase();
  if (!trimmed) return undefined;

  // URL() needs a scheme; tenants store both `https://x.com` and bare `x.com`.
  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//.test(trimmed) ? trimmed : `https://${trimmed}`;

  try {
    const host = new URL(withScheme).hostname;
    return host.startsWith('www.') ? host.slice(4) : host;
  } catch {
    return undefined;
  }
}
